import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { 
  Loader2, Mail, Phone, Calendar, User, 
  CheckCircle, Clock, Archive, Trash2, Eye, X
} from 'lucide-react';

const Messages = () => {
  const { token } = useAdmin();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (messageId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/messages/${messageId}`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, status }));
      }
      toast({
        title: "Estado atualizado",
        description: `Mensagem marcada como ${status}`
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o estado.",
        variant: "destructive"
      });
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta mensagem?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
      setSelectedMessage(null);
      toast({
        title: "Mensagem eliminada",
        description: "A mensagem foi eliminada com sucesso."
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível eliminar a mensagem.",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-600">Pendente</Badge>;
      case 'read':
        return <Badge className="bg-blue-600">Lida</Badge>;
      case 'replied':
        return <Badge className="bg-green-600">Respondida</Badge>;
      case 'archived':
        return <Badge className="bg-gray-600">Arquivada</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#B8860B]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Mensagens</h1>
          <p className="text-white/60">{messages.length} mensagens no total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {[
          { value: 'all', label: 'Todas' },
          { value: 'pending', label: 'Pendentes' },
          { value: 'read', label: 'Lidas' },
          { value: 'replied', label: 'Respondidas' },
          { value: 'archived', label: 'Arquivadas' }
        ].map(f => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
            className={filter === f.value 
              ? 'bg-[#B8860B] text-black' 
              : 'border-[#B8860B]/30 text-white hover:bg-[#B8860B]/20'}
          >
            {f.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          {filteredMessages.length === 0 ? (
            <Card className="bg-[#242424] border-[#B8860B]/20">
              <CardContent className="p-8 text-center">
                <Mail className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <p className="text-white/60">Nenhuma mensagem encontrada</p>
              </CardContent>
            </Card>
          ) : (
            filteredMessages.map(msg => (
              <Card 
                key={msg.id} 
                className={`bg-[#242424] border-[#B8860B]/20 cursor-pointer transition-all hover:border-[#B8860B]/50 ${selectedMessage?.id === msg.id ? 'border-[#B8860B]' : ''}`}
                onClick={() => {
                  setSelectedMessage(msg);
                  if (msg.status === 'pending') {
                    updateStatus(msg.id, 'read');
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#B8860B]" />
                      <span className="text-white font-medium">{msg.nome} {msg.sobrenome}</span>
                    </div>
                    {getStatusBadge(msg.status)}
                  </div>
                  <p className="text-white/60 text-sm mb-2 line-clamp-2">{msg.mensagem}</p>
                  <div className="flex items-center gap-4 text-xs text-white/40">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(msg.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {msg.email}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Message Detail */}
        {selectedMessage ? (
          <Card className="bg-[#242424] border-[#B8860B]/20 h-fit sticky top-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Detalhes da Mensagem</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedMessage(null)}
                className="text-white/60 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-[#B8860B]" />
                  <span className="text-white font-medium text-lg">
                    {selectedMessage.nome} {selectedMessage.sobrenome}
                  </span>
                </div>
                {getStatusBadge(selectedMessage.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-white/70">
                  <Mail className="w-4 h-4 text-[#B8860B]" />
                  <a href={`mailto:${selectedMessage.email}`} className="hover:text-[#B8860B]">
                    {selectedMessage.email}
                  </a>
                </div>
                {selectedMessage.telefone && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Phone className="w-4 h-4 text-[#B8860B]" />
                    <a href={`tel:${selectedMessage.telefone}`} className="hover:text-[#B8860B]">
                      {selectedMessage.telefone}
                    </a>
                  </div>
                )}
              </div>

              {selectedMessage.tipo_interesse && (
                <div className="text-sm">
                  <span className="text-white/50">Interesse: </span>
                  <span className="text-white">{selectedMessage.tipo_interesse}</span>
                </div>
              )}

              <div className="text-sm text-white/50">
                <Calendar className="w-4 h-4 inline mr-1" />
                {formatDate(selectedMessage.created_at)}
              </div>

              <div className="bg-[#1a1a1a] p-4 rounded-lg">
                <p className="text-white whitespace-pre-wrap">{selectedMessage.mensagem}</p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-[#B8860B]/20">
                <Button
                  size="sm"
                  onClick={() => updateStatus(selectedMessage.id, 'read')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 mr-1" />Marcar Lida
                </Button>
                <Button
                  size="sm"
                  onClick={() => updateStatus(selectedMessage.id, 'replied')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />Respondida
                </Button>
                <Button
                  size="sm"
                  onClick={() => updateStatus(selectedMessage.id, 'archived')}
                  variant="outline"
                  className="border-[#B8860B]/30 text-white hover:bg-[#B8860B]/20"
                >
                  <Archive className="w-4 h-4 mr-1" />Arquivar
                </Button>
                <Button
                  size="sm"
                  onClick={() => deleteMessage(selectedMessage.id)}
                  variant="outline"
                  className="border-red-500/50 text-red-500 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4 mr-1" />Eliminar
                </Button>
              </div>

              {/* Reply via Email */}
              <div className="pt-4">
                <Button
                  className="w-full bg-[#B8860B] hover:bg-[#D4A84B] text-black"
                  onClick={() => window.open(`mailto:${selectedMessage.email}?subject=Re: Contacto Santa Carne`, '_blank')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Responder por Email
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#242424] border-[#B8860B]/20 h-fit">
            <CardContent className="p-12 text-center">
              <Mail className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60">Selecione uma mensagem para ver os detalhes</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Messages;
